import { useEffect, useMemo, useState } from 'react';
import MissionCard from '@/components/cards/MissionCard';
import ParchmentCard from '@/components/ui/ParchmentCard';
import useAuthStore from '@/store/authStore';
import useGameStore from '@/store/gameStore';
import useNotificationStore from '@/store/notificationStore';
import * as missionService from '@/services/missionService';
import * as playerService from '@/services/playerService';

const groupMissions = (missions) => ({
  completed: missions.filter((mission) => mission.status === 'completed'),
  active: missions.filter((mission) => mission.status === 'in_progress'),
  claimed: missions.filter((mission) => mission.status === 'claimed'),
});

function MissionGroup({ title, empty, missions, onClaim, claimingId }) {
  return (
    <section className="mt-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-urban text-lg font-black text-accent">{title}</h2>
        <span className="rounded-full border border-edge bg-surfaceAlt px-3 py-1 text-xs font-bold text-muted">
          {missions.length}
        </span>
      </div>
      {missions.length === 0 ? (
        <ParchmentCard className="text-fg">{empty}</ParchmentCard>
      ) : (
        missions.map((mission) => (
          <MissionCard
            key={mission.id}
            playerMission={mission}
            onClaim={onClaim}
            claiming={claimingId === mission.id}
          />
        ))
      )}
    </section>
  );
}

export default function MissionsPage() {
  const [missions, setMissions] = useState([]);
  const [status, setStatus] = useState('A carregar missões...');
  const [claimingId, setClaimingId] = useState(null);
  const updatePlayer = useAuthStore((state) => state.updatePlayer);
  const openRewardSequence = useGameStore((state) => state.openRewardSequence);
  const setUnreadCount = useNotificationStore((state) => state.setUnreadCount);
  const grouped = useMemo(() => groupMissions(missions), [missions]);

  const applyMissions = (nextMissions) => {
    setMissions(nextMissions);
    setUnreadCount(nextMissions.filter((mission) => mission.status === 'completed').length);
  };

  const loadMissions = async () => {
    setStatus('A carregar missões...');
    try {
      const res = await playerService.getMissions();
      applyMissions(res.data.data || []);
      setStatus('');
    } catch (err) {
      setStatus(err.response?.data?.message || 'Ainda não foi possível carregar as missões.');
    }
  };

  useEffect(() => {
    loadMissions();
  }, []);

  const handleClaim = async (playerMission) => {
    setClaimingId(playerMission.id);
    try {
      const res = await missionService.claimMission(playerMission.missionId);
      const result = res.data.data;
      updatePlayer(result.player);
      openRewardSequence({
        reward: {
          poi: { name: result.mission?.title || 'Missão reclamada' },
          rewards: result.rewards,
        },
      });
      applyMissions(
        missions.map((mission) =>
          mission.id === playerMission.id ? { ...mission, status: 'claimed', claimedAt: new Date().toISOString() } : mission
        )
      );
    } catch (err) {
      setStatus(err.response?.data?.message || 'Não foi possível reclamar esta recompensa.');
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <main className="h-full overflow-y-auto bg-background px-4 pb-4 pt-4">
      <h1 className="font-urban text-2xl font-black text-fg">Missões</h1>
      <p className="mt-1 text-sm text-muted">Completa objetivos reais na cidade e reclama o teu tesouro.</p>

      {status && <ParchmentCard className="mt-4 text-fg">{status}</ParchmentCard>}

      <MissionGroup
        title="Por reclamar"
        empty="Ainda não há recompensas prontas para reclamar."
        missions={grouped.completed}
        onClaim={handleClaim}
        claimingId={claimingId}
      />
      <MissionGroup
        title="Ativas"
        empty="Não há missões ativas neste momento."
        missions={grouped.active}
        onClaim={handleClaim}
        claimingId={claimingId}
      />
      <MissionGroup
        title="Reclamadas"
        empty="Ainda não reclamaste nenhuma missão."
        missions={grouped.claimed}
        onClaim={handleClaim}
        claimingId={claimingId}
      />
    </main>
  );
}
